import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CONNECTION_ERROR_CODES,
    DataMaintenanceLocationP58Model,
    DataMaintenanceModel,
    DataMaintenanceUtilities,
    SynchronizationService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { OdataDataMaintenanceService } from './odata-data-maintenance.service';
import { cloneDeep } from 'lodash';

@Component({
    selector: 'pace-data-maintenance-location',
    templateUrl: './data-maintenance-location.component.html',
    styleUrls: ['./data-maintenance-location.component.scss'],
})
export class DataMaintenanceLocationComponent implements OnInit, OnDestroy {
    @Input()
    public model: DataMaintenanceModel | DataMaintenanceLocationP58Model;

    public isNgmP58: boolean;
    public displayTechnicalIdNum: boolean;
    public displayReasonText: boolean;

    public strings: {
        footer: string;
    };

    public formChildren: FormArray;
    public dataMaintenanceLocationForm: FormGroup;
    public isLoading = false;
    public destroyTrigger = new Subject<void>();
    public oldModel: DataMaintenanceLocationP58Model;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly odataDataMaintenanceService: OdataDataMaintenanceService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly synchronizationService: SynchronizationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly dataMaintenanceUtilities: DataMaintenanceUtilities,
    ) {}

    public ngOnInit(): void {
        this.isNgmP58 = this.clientManager.isNgmP58();
        this.displayTechnicalIdNum = this.clientManager.isNgmAA();
        this.displayReasonText = this.clientManager.isNgmAA();
        this.strings = {
            footer: this.clientManager.isNgmAA()
                ? <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_LOCATION_REPORT_TO_DATA_ADMIN')
                : this.isNgmP58
                  ? <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_LOCATION_SEND_DATA')
                  : <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_LOCATION_CHANGE_DATA'),
        };

        this.formChildren = this.formBuilder.array([]);
        this.dataMaintenanceLocationForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
        if (this.model instanceof DataMaintenanceLocationP58Model) {
            this.oldModel = _.cloneDeep(this.model);
        }
    }

    public ngOnDestroy(): void {
        this.destroyTrigger.next();
    }

    public submit(): void {
        if (this.model instanceof DataMaintenanceLocationP58Model) {
            this.dataMaintenanceUtilities.updateRecipientListInLocalStorage(cloneDeep(this.model));
            this.dataMaintenanceUtilities.openMailToSubmitData(this.model, this.oldModel, this.oldModel.isInvitro);
            this.modalInstance.hide();
            return;
        }
        this.isLoading = true;
        this.odataDataMaintenanceService
            .sendDataMaintenanceEquipment(this.model)
            .pipe(
                mergeMap(() => {
                    this.modalInstance.hide();
                    this.log.debug('DataMaintenanceLocationComponent -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(`DataMaintenanceLocationComponent: submit: Error: ${JSON.stringify(error)},
                    ${JSON.stringify(this.model.getOutModel())}`);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_SYNC_PROBLEM'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : JSON.stringify(error),
                    );
                    this.isLoading = false;
                    return of(undefined);
                }),
                takeUntil(this.destroyTrigger),
            )
            .subscribe();
    }
}
